import { api } from './client'

/**
 * F-081 (TASK-0335, REQ-221-001, REQ-256-001/002).
 *
 * 1) Template di configurazione (`/api/configuration-templates`, solo Admin): si salva la configurazione
 *    corrente come template, lo si pubblica e lo si applica a un ambiente dopo averne visto l'anteprima.
 *    L'applicazione crea SOLO ciò che manca, in Bozza o inattivo; non sovrascrive e non cancella mai.
 * 2) Snapshot dei riferimenti di configurazione di una pratica (`/api/contracts/{id}/configuration-snapshots`).
 *    Solo riferimenti (versioni, codici, impronta), mai importi.
 */

export type TemplateStatus = 'Bozza' | 'Pubblicato' | 'Archiviato'

export type BusinessModel =
  | 'AgenziaTelecomunicazioni'
  | 'AgenziaEnergia'
  | 'CallCenter'
  | 'AgenziaMultiutility'
  | 'Altro'

export const BUSINESS_MODEL_LABELS: Record<BusinessModel, string> = {
  AgenziaTelecomunicazioni: 'Agenzia telecomunicazioni',
  AgenziaEnergia: 'Agenzia energia',
  CallCenter: 'Call center',
  AgenziaMultiutility: 'Agenzia multiutility',
  Altro: 'Altro',
}

export type TemplateSectionKey =
  | 'pipelines'
  | 'callOutcomes'
  | 'dynamicFields'
  | 'automations'
  | 'documentTemplates'
  | 'validationRules'
  // F-156 (REQ-256-002): sezioni estese, presenti solo con `Configuration:Templates:ExtendedSections` acceso.
  | 'userCategories'
  | 'dashboardPolicies'
  | 'savedViews'
  | 'tasks'

export const TEMPLATE_SECTION_LABELS: Record<TemplateSectionKey, string> = {
  pipelines: 'Pipeline e fasi',
  callOutcomes: 'Esiti chiamata',
  dynamicFields: 'Campi dinamici',
  automations: 'Automazioni',
  documentTemplates: 'Modelli documentali',
  validationRules: 'Regole di completezza',
  userCategories: 'Ruoli (categorie utente, create inattive)',
  dashboardPolicies: 'Dashboard (widget ammessi per ruolo)',
  savedViews: 'Report condivisi (viste Team e Aziendali)',
  // Voce informativa: i task nascono dalle automazioni con azione CreaTask, non sono un'entità del template.
  tasks: 'Task (automazioni che creano task)',
}

export const TEMPLATE_SECTIONS = Object.keys(TEMPLATE_SECTION_LABELS) as TemplateSectionKey[]

export type TemplateCounts = Record<TemplateSectionKey, number>

export interface ConfigurationTemplateSummary {
  id: number
  name: string
  businessModel: BusinessModel
  description: string | null
  version: number
  status: TemplateStatus
  createdAt: string
  updatedAt: string | null
  createdBy: number | null
  counts: TemplateCounts | null
}

export interface ConfigurationTemplate extends ConfigurationTemplateSummary {
  payload: Record<string, unknown>
}

/** Che cosa farebbe l'applicazione su un elemento. */
export type PlanAction = 'Crea' | 'Identico' | 'Conflitto' | 'RiferimentoMancante'

export const PLAN_ACTION_LABELS: Record<PlanAction, string> = {
  Crea: 'Da creare',
  Identico: 'Già presente, identico',
  Conflitto: 'Conflitto (non si tocca)',
  RiferimentoMancante: 'Riferimento mancante',
}

export const PLAN_ACTION_BADGES: Record<PlanAction, string> = {
  Crea: 'bg-success',
  Identico: 'bg-secondary',
  Conflitto: 'bg-warning text-dark',
  RiferimentoMancante: 'bg-danger',
}

export interface PlanItem {
  key: string
  action: PlanAction
  reason: string | null
}

export interface PlanSection {
  section: TemplateSectionKey
  create: number
  identical: number
  conflicts: number
  missingReferences: number
  items: PlanItem[]
}

export interface TemplatePreview {
  templateId: number
  name: string
  version: number
  status: TemplateStatus
  plan: { totalCreate: number; sections: PlanSection[] }
}

export interface ApplyDetail {
  key: string
  action: PlanAction | 'Errore'
  reason: string | null
}

export interface ApplySection {
  section: TemplateSectionKey
  created: number
  identical: number
  conflicts: number
  missingReferences: number
  failed: number
  details: ApplyDetail[]
}

export interface ApplyReport {
  templateId: number
  name: string
  version: number
  created: number
  sections: ApplySection[]
  moduleErrors: { module: string; error: string }[]
}

export interface SnapshotRequest {
  name: string
  businessModel: BusinessModel
  description?: string | null
}

export const configurationTemplatesApi = {
  list: (status?: TemplateStatus) =>
    api<{ items: ConfigurationTemplateSummary[] }>(
      `/api/configuration-templates${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  get: (id: number) => api<ConfigurationTemplate>(`/api/configuration-templates/${id}`),
  snapshot: (data: SnapshotRequest) =>
    api<ConfigurationTemplate>('/api/configuration-templates/snapshot', { method: 'POST', body: JSON.stringify(data) }),
  publish: (id: number) =>
    api<ConfigurationTemplate>(`/api/configuration-templates/${id}/publish`, { method: 'POST' }),
  archive: (id: number) =>
    api<ConfigurationTemplate>(`/api/configuration-templates/${id}/archive`, { method: 'POST' }),
  newVersion: (id: number) =>
    api<ConfigurationTemplate>(`/api/configuration-templates/${id}/new-version`, { method: 'POST' }),
  remove: (id: number) => api<void>(`/api/configuration-templates/${id}`, { method: 'DELETE' }),
  preview: (id: number) => api<TemplatePreview>(`/api/configuration-templates/${id}/preview`),
  apply: (id: number) => api<ApplyReport>(`/api/configuration-templates/${id}/apply`, { method: 'POST' }),
}

// --- Snapshot della pratica -----------------------------------------------------------------

export type SnapshotTrigger = 'Creazione' | 'Invio'

export interface SnapshotEntityRef {
  id: number
  /** Nulla se l'entità non ha ancora nessuna versione registrata nel catalogo. */
  version: number | null
}

export interface ContractConfigurationRefs {
  product: SnapshotEntityRef | null
  operator: SnapshotEntityRef | null
  department: SnapshotEntityRef | null
  dynamicFields: { code: string; version: number }[]
  validationRules: { count: number; fingerprint: string }
  commissionPlanVersion: number | null
}

export interface ContractConfigurationSnapshot {
  id: number
  contractId: number
  trigger: SnapshotTrigger
  capturedAt: string
  refs: ContractConfigurationRefs | null
}

export const contractConfigurationApi = {
  snapshots: (contractId: number) =>
    api<ContractConfigurationSnapshot[]>(`/api/contracts/${contractId}/configuration-snapshots`),
}
